import { supabase, isSupabaseConfigured } from './supabase.js';
import { authState } from './auth.js';

const KEYS = {
  cart: 'forgelink_cart',
  enquiries: 'forgelink_enquiries',
  orders: 'forgelink_orders',
};

function read(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function write(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

export function getCart() {
  return read(KEYS.cart, []);
}

export function setCart(items) {
  write(KEYS.cart, items);
}

export function getLocalEnquiries() {
  return read(KEYS.enquiries, []);
}

export function getLocalOrders() {
  return read(KEYS.orders, []);
}

export async function saveEnquiry(enquiry) {
  const withMeta = {
    ...enquiry,
    id: enquiry.id || crypto.randomUUID(),
    status: 'new',
    createdAt: enquiry.createdAt || new Date().toISOString(),
  };

  const list = getLocalEnquiries();
  list.unshift(withMeta);
  write(KEYS.enquiries, list);

  if (isSupabaseConfigured && supabase) {
    try {
      await supabase.from('enquiries').insert({
        id: withMeta.id,
        product_id: withMeta.productId,
        product_name: withMeta.productName,
        factory_name: withMeta.factoryName,
        company: withMeta.company,
        contact_name: withMeta.contact,
        email: withMeta.email,
        phone: withMeta.phone || null,
        quantity: withMeta.quantity,
        message: withMeta.message,
        status: withMeta.status,
        user_id: authState.user?.id || null,
      });
    } catch (err) {
      console.warn('Supabase enquiry sync skipped:', err);
    }
  }

  return withMeta;
}

export async function saveOrder(order) {
  const withMeta = {
    ...order,
    id: order.id || `FL-${Date.now().toString(36).toUpperCase()}`,
    status: 'placed',
    createdAt: order.createdAt || new Date().toISOString(),
  };

  const list = getLocalOrders();
  list.unshift(withMeta);
  write(KEYS.orders, list);
  setCart([]);

  if (isSupabaseConfigured && supabase) {
    try {
      await supabase.from('orders').insert({
        id: withMeta.id,
        company: withMeta.company,
        contact_name: withMeta.contact,
        email: withMeta.email,
        phone: withMeta.phone || null,
        ship_address: withMeta.address,
        delivery_notes: withMeta.notes || null,
        merchandise_total: withMeta.merchandise,
        freight_total: withMeta.freight,
        grand_total: withMeta.total,
        status: withMeta.status,
        user_id: authState.user?.id || null,
      });
      if (withMeta.items?.length) {
        await supabase.from('order_items').insert(
          withMeta.items.map((it) => ({
            order_id: withMeta.id,
            product_id: it.productId,
            product_name: it.name,
            factory_name: it.factory,
            sku: it.sku,
            unit_price: it.unitPrice,
            quantity: it.qty,
            line_total: it.lineTotal,
          })),
        );
      }
    } catch (err) {
      console.warn('Supabase order sync skipped:', err);
    }
  }

  return withMeta;
}

export async function fetchRemoteEnquiries() {
  if (!isSupabaseConfigured || !supabase) return getLocalEnquiries();
  try {
    const { data, error } = await supabase
      .from('enquiries')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map((row) => ({
      id: row.id,
      productId: row.product_id,
      productName: row.product_name,
      factoryName: row.factory_name,
      company: row.company,
      contact: row.contact_name,
      email: row.email,
      phone: row.phone,
      quantity: row.quantity,
      message: row.message,
      status: row.status,
      createdAt: row.created_at,
    }));
  } catch (err) {
    console.warn('Falling back to local enquiries:', err);
    return getLocalEnquiries();
  }
}

export async function fetchRemoteOrders() {
  if (!isSupabaseConfigured || !supabase) return getLocalOrders();
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('*, order_items(*)')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map((row) => ({
      id: row.id,
      company: row.company,
      contact: row.contact_name,
      email: row.email,
      phone: row.phone,
      address: row.ship_address,
      notes: row.delivery_notes,
      merchandise: Number(row.merchandise_total),
      freight: Number(row.freight_total),
      total: Number(row.grand_total),
      status: row.status,
      createdAt: row.created_at,
      items: (row.order_items || []).map((it) => ({
        productId: it.product_id,
        name: it.product_name,
        factory: it.factory_name,
        sku: it.sku,
        unitPrice: Number(it.unit_price),
        qty: it.quantity,
        lineTotal: Number(it.line_total),
      })),
    }));
  } catch (err) {
    console.warn('Falling back to local orders:', err);
    return getLocalOrders();
  }
}
